import React, { useEffect, useState } from "react";
import { Instagram, MapPin } from "lucide-react";
import { api } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { getTheme } from "@/lib/theme";

export default function About() {
  const { selectedBusinessId } = useAuth();
  const theme = getTheme(selectedBusinessId);
  const [info, setInfo] = useState(null);

  useEffect(() => {
    if (!selectedBusinessId) return;
    api.getPaymentInfo(selectedBusinessId).then(setInfo).catch(() => {});
  }, [selectedBusinessId]);

  return (
    <div>
      <section className="hero-gradient">
        <div className="container-app py-16">
          <div className="text-xs uppercase tracking-[0.35em] mb-2" style={{ color: "var(--accent)" }}>{theme.kind}</div>
          <h1 className="font-display text-4xl sm:text-5xl" style={{ color: "var(--hero-fg)" }}>{info?.bakery_name || theme.name}</h1>
          <p className="mt-3 text-lg max-w-xl" style={{ color: "color-mix(in srgb, var(--hero-fg) 78%, transparent)" }}>{info?.tagline || theme.tagline}</p>
        </div>
      </section>

      <div className="container-app py-12 grid lg:grid-cols-[1fr_320px] gap-10">
        <div>
          <h2 className="font-display text-2xl mb-3" style={{ color: "var(--ink)" }}>Our story</h2>
          <p className="leading-relaxed" style={{ color: "var(--muted)" }}>{info?.about || "Loading…"}</p>

          <h2 className="font-display text-2xl mt-10 mb-3" style={{ color: "var(--ink)" }}>How ordering works</h2>
          <ol className="space-y-3">
            {[
              "Browse the menu and customize your order.",
              "Place your order and pay a small deposit to reserve it.",
              "We prepare it fresh — track the status in real time.",
              "Pay the remaining balance when it's ready for pickup or delivery.",
            ].map((s, i) => (
              <li key={i} className="flex gap-3">
                <span className="h-7 w-7 rounded-full flex items-center justify-center text-sm font-bold shrink-0" style={{ background: "var(--accent-soft)", color: "var(--brand)" }}>{i + 1}</span>
                <span style={{ color: "var(--ink)" }}>{s}</span>
              </li>
            ))}
          </ol>
        </div>

        <aside className="rounded-2xl p-6 h-fit" style={{ background: "var(--surface)", border: "1px solid var(--border)" }}>
          <h3 className="font-display text-xl mb-4" style={{ color: "var(--ink)" }}>Pay & connect</h3>
          {info?.zelle_handle && <Line label="Zelle" value={info.zelle_handle} />}
          {info?.zelle_name && <Line label="Zelle name" value={info.zelle_name} />}
          {info?.cashapp_handle && <Line label="CashApp" value={info.cashapp_handle} />}
          {info?.delivery_address && (
            <div className="flex items-start gap-2 mt-3 text-sm" style={{ color: "var(--ink)" }}>
              <MapPin size={16} className="mt-0.5" style={{ color: "var(--muted)" }} /> {info.delivery_address}
            </div>
          )}
          {info?.instagram_handle && (
            <a href={`https://instagram.com/${info.instagram_handle}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 mt-4 font-semibold" style={{ color: "var(--brand)" }}>
              <Instagram size={16} /> @{info.instagram_handle}
            </a>
          )}
        </aside>
      </div>
    </div>
  );
}

function Line({ label, value }) {
  return (
    <div className="flex justify-between text-sm py-1.5" style={{ borderBottom: "1px solid var(--border)" }}>
      <span style={{ color: "var(--muted)" }}>{label}</span>
      <span className="font-semibold" style={{ color: "var(--ink)" }}>{value}</span>
    </div>
  );
}
